import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore
} from 'react';
import moment from 'moment';
import { v4 as uuid } from 'uuid';

import { submitOverwatchAction } from 'api/overwatch';
import { useTypedSelector } from 'hooks';

import { OverwatchActionKey } from '../widgets/types';
import { actionFailureView } from './overwatchActions';
import {
  clearActionSubmissions,
  EMPTY_SUBMISSION,
  IActionSubmission,
  readSubmission,
  subscribeSubmissions,
  writeSubmission
} from './submissionStore';

export type { IActionSubmission };
export { clearActionSubmissions };

export const sameInput = (left: Record<string, unknown>, right: Record<string, unknown>) =>
  JSON.stringify(left) === JSON.stringify(right);

export interface IActionScope {
  track: (slot: string) => AbortController;
  release: (controller: AbortController) => void;
  /** Stops pending resends. A POST already sent finishes unread; its slot keeps the id and input. */
  cancel: () => void;
}

export const createActionScope = (): IActionScope => {
  const runs = new Map<AbortController, string>();

  return {
    track: slot => {
      const controller = new AbortController();

      runs.set(controller, slot);

      return controller;
    },
    release: controller => {
      runs.delete(controller);
    },
    cancel: () => {
      runs.forEach((slot, controller) => {
        controller.abort();
        writeSubmission(slot, { status: 'idle' });
      });
      runs.clear();
    }
  };
};

// Outside any page scope (a section rendered on its own) nothing is ever cancelled.
const ActionScopeContext = createContext<IActionScope>(createActionScope());

/** Wraps a page (Overwatch, Orbit): leaving it cancels the resends its submissions still owe. */
export const OverwatchActionScope: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [scope] = useState(createActionScope);

  useEffect(() => () => scope.cancel(), [scope]);

  return <ActionScopeContext.Provider value={scope}>{children}</ActionScopeContext.Provider>;
};

interface ISubmitOptions {
  context?: unknown;
  /** Leave the control drawn on a 403, for a caller that decides itself (the wizard's origin). */
  keepOnForbidden?: boolean;
}

const submitToSlot = async (
  slot: string,
  key: OverwatchActionKey,
  input: Record<string, unknown>,
  scope: IActionScope,
  { context = null, keepOnForbidden = false }: ISubmitOptions
) => {
  const current = readSubmission(slot);

  if (current.status === 'sending') return;

  const clientRequestId =
    current.clientRequestId && current.input && sameInput(current.input, input)
      ? current.clientRequestId
      : uuid();
  const controller = scope.track(slot);

  writeSubmission(slot, {
    status: 'sending',
    clientRequestId,
    input,
    context,
    result: null,
    sentAt: null,
    failure: null,
    acknowledged: false
  });

  const outcome = await submitOverwatchAction(
    key,
    { client_request_id: clientRequestId, input },
    controller.signal
  );

  scope.release(controller);

  if (outcome.kind === 'cancelled' || controller.signal.aborted) return;

  if (outcome.kind === 'ok') {
    writeSubmission(slot, {
      status: 'succeeded',
      result: outcome.data,
      sentAt: moment().format(),
      retryAt: null
    });

    return;
  }

  const view = actionFailureView(outcome);

  writeSubmission(slot, {
    status: 'failed',
    failure: { kind: outcome.kind, ...view },
    clientRequestId: outcome.kind === 'idConflict' ? uuid() : clientRequestId,
    retryAt: view.retryAfterMs === null ? null : Date.now() + view.retryAfterMs,
    hidden: outcome.kind === 'forbidden' && !keepOnForbidden,
    discoveryStale: outcome.kind === 'forbidden'
  });
};

export interface IActionSubmissionHandle {
  submission: IActionSubmission;
  submit: (input: Record<string, unknown>, options?: ISubmitOptions) => void;
  update: (changes: Partial<IActionSubmission>) => void;
  /** A new submission: everything but the 403 hide and the 50332 wait is dropped. */
  startOver: () => void;
}

export const useActionSubmission = (key: OverwatchActionKey): IActionSubmissionHandle => {
  const userId = useTypedSelector(state => state.global?.authUser?.id);
  const tenantId = useTypedSelector(state => state.global?.tenantConfig?.id);
  const slot = `${userId ?? ''}:${tenantId ?? ''}:${key}`;
  const scope = useContext(ActionScopeContext);
  const submission = useSyncExternalStore(subscribeSubmissions, () => readSubmission(slot));

  const submit = useCallback(
    (input: Record<string, unknown>, options: ISubmitOptions = {}) => {
      submitToSlot(slot, key, input, scope, options);
    },
    [slot, key, scope]
  );
  const update = useCallback(
    (changes: Partial<IActionSubmission>) => writeSubmission(slot, changes),
    [slot]
  );
  const startOver = useCallback(() => {
    const { status, hidden, retryAt } = readSubmission(slot);

    if (status !== 'sending') writeSubmission(slot, { ...EMPTY_SUBMISSION, hidden, retryAt });
  }, [slot]);

  return { submission, submit, update, startOver };
};
